import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import backend from '../services/backend';
import { useToast } from '../components/Toast';
import { entityViewTitle } from '../components/ID';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { Header } from '../components/reconstructedDocument/Header';
import { OverridesCard } from '../components/reconstructedDocument/OverridesCard';
import { SourceDocumentsCard } from '../components/reconstructedDocument/SourceDocumentsCard';
import { ConstituentPagesCard } from '../components/reconstructedDocument/ConstituentPagesCard';
import { ConsolidatedExtractionCard } from '../components/reconstructedDocument/ConsolidatedExtractionCard';
import { PageProcessingSummary } from '../components/PageProcessingSummary';
import { useAuth } from '../hooks/useAuth';
import { FindingRevertContext } from '../components/supervisorFindingRevert';
import { useEntityFindingRevert } from '../components/reconstructedDocument/useEntityFindingRevert';
import {
  aggregateConstituentPageProcessing,
  hasUnfinishedPageWork,
} from '../lib/aggregateConstituentPageProcessing';

export function ReconstructedDocumentContent({
  id,
  embedded = false,
}: {
  id: string;
  embedded?: boolean;
}) {
  const toast = useToast();
  const { hasAccess } = useAuth();
  const findingRevert = useEntityFindingRevert();

  const [reextracting, setReextracting] = useState(false);
  const [reextractCount, setReextractCount] = useState<{ enqueued: number; total: number } | null>(null);
  const reextractTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idRef = useRef(id);
  idRef.current = id;

  const query = useQuery({
    queryKey: ['reconstructed-document', id],
    queryFn: async () => {
      const res = await backend.getReconstructedDocument(id!);
      return res.data.item;
    },
    enabled: !!id,
    // Re-extract forces a tight 3s cadence for 30s. Outside of that, if any
    // constituent page still has work in flight (pipeline running on a
    // newly-linked source doc, or stages catching up), poll every 5s until
    // everything reaches a terminal state. Otherwise stay idle.
    refetchInterval: (q) => {
      if (reextracting) return 3000;
      const entity = q.state.data;
      if (!entity) return false;
      return hasUnfinishedPageWork(entity) ? 5000 : false;
    },
  });

  // Embedded (modal) mode passes undefined so the hook is a no-op and the host
  // project page keeps its own document.title.
  useDocumentTitle(
    embedded ? undefined : entityViewTitle(id, query.data?.suggestedFilename ?? 'Reconstructed Document'),
  );

  const autoRefreshing =
    !reextracting && !!query.data && hasUnfinishedPageWork(query.data);

  // Reset polling state when navigating to a different reconstructed document.
  // react-router reuses the same page instance across param-only route changes,
  // so without this the new entity's query keeps a 3s refetchInterval and the
  // "Refreshing extraction…" indicator that was triggered for the previous id.
  useEffect(() => {
    setReextracting(false);
    setReextractCount(null);
    if (reextractTimerRef.current !== null) {
      clearTimeout(reextractTimerRef.current);
      reextractTimerRef.current = null;
    }
    return () => {
      if (reextractTimerRef.current !== null) {
        clearTimeout(reextractTimerRef.current);
        reextractTimerRef.current = null;
      }
    };
  }, [id]);

  // Bug 2 fix: kick off the POST first, then enter polling state only on success.
  // Without this, a failed POST (404/409/network) leaves the UI polling for 30s
  // with no error feedback.
  // Bug 1 fix: drop the updatedAt-advancement early-stop. The consolidated-
  // extraction refresh in dataExtraction.handler.ts bumps updatedAt on every
  // constituent page completion, not just the last one, so the previous check
  // stopped polling after the FIRST page finished. Rely on the 30s ceiling instead.
  const handleReextract = async () => {
    if (!query.data) return;
    const targetId = query.data.id;
    const totalPages = query.data.pages.length;
    let enqueuedCount: number;
    try {
      const res = await backend.reextractReconstructedDocument(targetId);
      enqueuedCount = res.data.enqueued.length;
    } catch (e) {
      console.error('re-extract POST failed', e);
      // API returns { error: { message, data } } — read .message, not the bare object.
      const axiosErr = e as {
        response?: { data?: { error?: { message?: string } } };
        message?: string;
      };
      const message =
        axiosErr.response?.data?.error?.message ??
        axiosErr.message ??
        'Re-extract failed';
      toast.error(`Re-extract failed: ${message}`);
      return;
    }
    // The user may have navigated to a different /reconstructed-documents/:id
    // while the POST was in flight. Skip enabling polling in that case so the
    // new entity's query doesn't get the 30s refetchInterval that was meant
    // for the prior id.
    if (idRef.current !== targetId) return;
    // Surface partial fan-outs so operators don't assume every page got reset.
    if (enqueuedCount < totalPages) {
      toast.error(`Re-extracted ${enqueuedCount} of ${totalPages} pages (some were skipped or failed to enqueue)`);
    }
    if (reextractTimerRef.current !== null) {
      clearTimeout(reextractTimerRef.current);
    }
    setReextractCount({ enqueued: enqueuedCount, total: totalPages });
    setReextracting(true);
    reextractTimerRef.current = setTimeout(() => {
      reextractTimerRef.current = null;
      setReextracting((on) => (on ? false : on));
      setReextractCount(null);
    }, 30000);
  };

  if (!id) {
    return <div className="p-6 text-red-600">Missing id in URL</div>;
  }

  if (query.isLoading) {
    return <div className="p-6 text-gray-500 dark:text-zinc-400">Loading…</div>;
  }

  if (query.isError || !query.data) {
    return (
      <div className="p-6 text-red-600 dark:text-red-400">
        Failed to load reconstructed document: {(query.error as Error)?.message ?? 'unknown error'}
      </div>
    );
  }

  const entity = query.data;

  return (
    <div className="space-y-4">
      <Header entity={entity} embedded={embedded} />
      {/* Narrow summary cards share a single row at lg+ to reclaim the
        ~2/3 of horizontal space they leave empty when stacked. Wide cards
        (constituent thumbnail strip, extraction tables) stay full width
        so they don't have to fight for room. */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
        <OverridesCard
          entity={entity}
          reextracting={reextracting}
          reextractCount={reextractCount}
          onReextract={handleReextract}
        />
        <SourceDocumentsCard entity={entity} />
        <PageProcessingSummary
          processing={aggregateConstituentPageProcessing(entity)}
          collapsibleStorageKey="reconstructedDocumentView.collapsible.pageProcessing"
          supervisorRun={entity.supervisorRun}
        />
      </div>
      <ConstituentPagesCard entity={entity} />
      <FindingRevertContext.Provider value={hasAccess('admin') ? findingRevert : null}>
        <ConsolidatedExtractionCard
          entity={entity}
          refreshing={reextracting}
          autoRefreshing={autoRefreshing}
        />
      </FindingRevertContext.Provider>
    </div>
  );
}

export default function ReconstructedDocumentView() {
  const { id } = useParams<{ id: string }>();
  return <ReconstructedDocumentContent id={id!} />;
}
